import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, type TournamentSummary } from "../../lib/api";
import { preparationApi } from "../../lib/preparationApi";
import { COMMON_EVENT_TIMEZONES, tournamentTimezoneApi, type TournamentTimezoneRow } from "../../lib/tournamentTimezoneApi";
import { NewTournamentDialog } from "./NewTournamentDialog";
import "./season-calendar.css";

const CURRENT_YEAR = new Date().getFullYear();

function isoDate(value: string) {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : "";
}

function parseShortDate(value: string, season: number) {
  const match = value.trim().match(/^(\d{1,2})\.(\d{1,2})$/);
  if (!match) return "";
  const day = Number(match[1]);
  const month = Number(match[2]);
  const date = new Date(Date.UTC(season, month - 1, day));
  if (date.getUTCFullYear() !== season || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return "";
  return `${season}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

type ImportRow = {
  name: string;
  startDate: string;
  endDate: string;
  location: string;
  country: string;
  timezone: string;
};

function parseImport(text: string, season: number): { rows: ImportRow[]; errors: string[] } {
  const rows: ImportRow[] = [];
  const errors: string[] = [];
  text.split(/\r?\n/).forEach((source, index) => {
    const line = source.trim();
    if (!line) return;

    const pipe = line.split("|").map((part) => part.trim());
    if (pipe.length >= 3) {
      const startDate = isoDate(pipe[0]);
      const endDate = isoDate(pipe[1]);
      const name = pipe[2] || "";
      if (!startDate || !endDate || !name) {
        errors.push(`Wiersz ${index + 1}: użyj YYYY-MM-DD | YYYY-MM-DD | Nazwa`);
        return;
      }
      rows.push({ name, startDate, endDate, location: pipe[3] || "", country: pipe[4] || "", timezone: pipe[5] || "" });
      return;
    }

    const compactSource = pipe[0] || line;
    const timezone = pipe[1] || "";
    const compact = compactSource.match(/^(\d{1,2}\.\d{1,2})\s*[-–]\s*(\d{1,2}\.\d{1,2})\s+(.+)$/);
    if (compact) {
      const startDate = parseShortDate(compact[1], season);
      const endDate = parseShortDate(compact[2], season);
      const name = compact[3].trim();
      if (!startDate || !endDate || !name) {
        errors.push(`Wiersz ${index + 1}: nie rozpoznałem daty.`);
        return;
      }
      rows.push({ name, startDate, endDate, location: "", country: "", timezone });
      return;
    }

    errors.push(`Wiersz ${index + 1}: nie rozpoznałem formatu.`);
  });
  return { rows, errors };
}

function dateLabel(start: string, end: string) {
  if (!start) return "Brak daty";
  const format = (value: string) => {
    const date = new Date(`${value}T12:00:00`);
    return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat("pl-PL", { day: "2-digit", month: "2-digit" }).format(date);
  };
  return end && end !== start ? `${format(start)}–${format(end)}` : format(start);
}

function isoTodayInTimezone(timezone: string) {
  if (!timezone) return "";
  try {
    const parts = new Intl.DateTimeFormat("en-CA", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date());
    const value = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return `${value.year}-${value.month}-${value.day}`;
  } catch {
    return "";
  }
}

function weekBoundsFromIso(iso: string) {
  const date = new Date(`${iso}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return null;
  const day = (date.getUTCDay() + 6) % 7;
  const monday = new Date(date);
  monday.setUTCDate(date.getUTCDate() - day);
  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);
  const asIso = (value: Date) => value.toISOString().slice(0, 10);
  return { monday: asIso(monday), sunday: asIso(sunday) };
}

function inCurrentWeek(item: TournamentSummary, timezone: string) {
  const localToday = isoTodayInTimezone(timezone);
  const bounds = localToday ? weekBoundsFromIso(localToday) : null;
  if (!bounds) return false;
  const start = item.startDate || "9999-12-31";
  const end = item.endDate || item.startDate || "0000-01-01";
  return start <= bounds.sunday && end >= bounds.monday;
}

export function SeasonCalendarPage() {
  const queryClient = useQueryClient();
  const [season, setSeason] = useState(CURRENT_YEAR);
  const [creating, setCreating] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [timezoneDrafts, setTimezoneDrafts] = useState<Record<string, string>>({});

  const tournamentsQuery = useQuery({
    queryKey: ["tournaments", season],
    queryFn: () => api.tournaments(season),
    staleTime: 15_000,
  });
  const timezonesQuery = useQuery({
    queryKey: ["tournament-timezones", season],
    queryFn: () => tournamentTimezoneApi.list(season),
    staleTime: 15_000,
  });

  const tournaments = tournamentsQuery.data?.tournaments || [];
  const sorted = useMemo(() => [...tournaments].sort((a, b) => (a.startDate || "9999").localeCompare(b.startDate || "9999") || a.name.localeCompare(b.name, "pl")), [tournaments]);
  const parsed = useMemo(() => parseImport(importText, season), [importText, season]);
  const timezoneById = useMemo(() => new Map<string, TournamentTimezoneRow>((timezonesQuery.data?.tournaments || []).map((row) => [row.id, row])), [timezonesQuery.data]);

  const bulkCreate = useMutation({
    mutationFn: async () => {
      if (!parsed.rows.length || parsed.errors.length) throw new Error("Popraw listę przed importem.");
      const created: string[] = [];
      for (const row of parsed.rows) {
        const { timezone, ...createRow } = row;
        const result = await preparationApi.createTournament({ ...createRow, season, copyFromTournamentId: "", copyStaff: false });
        created.push(result.tournament.id);
        if (timezone.trim()) {
          await tournamentTimezoneApi.save(result.tournament.id, result.configRevision, timezone.trim());
        }
      }
      return created;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["tournaments", season] }),
        queryClient.invalidateQueries({ queryKey: ["tournament-timezones", season] }),
      ]);
      setImportText("");
      setImportOpen(false);
    },
  });

  const saveTimezone = useMutation({
    mutationFn: ({ tournamentId, expectedRevision, timezone }: { tournamentId: string; expectedRevision: number; timezone: string }) => tournamentTimezoneApi.save(tournamentId, expectedRevision, timezone.trim()),
    onSuccess: async (result) => {
      setTimezoneDrafts((current) => ({ ...current, [result.tournament.id]: result.tournament.timezone }));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["tournament-timezones", season] }),
        queryClient.invalidateQueries({ queryKey: ["tournament-setup", result.tournament.id] }),
        queryClient.invalidateQueries({ queryKey: ["round-workspace", result.tournament.id] }),
      ]);
    },
  });

  const error = tournamentsQuery.error || timezonesQuery.error || bulkCreate.error || saveTimezone.error;
  const errorMessage = error instanceof ApiError ? error.message : error instanceof Error ? error.message : "";

  return (
    <section className="page-grid prep-page season-calendar-page">
      <article className="panel season-calendar-hero">
        <div>
          <span className="eyebrow dark">KALENDARZ OPERACYJNY</span>
          <h2>Sezon {season}</h2>
          <p>Daty wybierają turniej tygodnia, a strefa czasowa steruje zegarem bota: Tee Times, publikacją rund, Powrotem oraz przypomnieniami poniedziałek/środa.</p>
        </div>
        <div className="season-calendar-actions">
          <label>
            <span>Sezon</span>
            <input type="number" min={2020} max={2100} value={season} onChange={(event) => setSeason(Number(event.target.value) || CURRENT_YEAR)} />
          </label>
          <button type="button" className="secondary-button" onClick={() => setSeason((value) => value + 1)}>+ następny sezon</button>
          <button type="button" className="secondary-button" onClick={() => setImportOpen((value) => !value)}>Importuj listę</button>
          <button type="button" className="primary-button" onClick={() => setCreating(true)}>+ Nowy turniej</button>
        </div>
      </article>

      {importOpen ? (
        <article className="panel season-calendar-import">
          <header><div><span className="eyebrow">IMPORT SEZONU</span><h3>Wklej kalendarz</h3></div></header>
          <p>Prosto: <code>08.01-11.01 Dubai Invitational | Asia/Dubai</code>. Pełny format: <code>2027-01-08 | 2027-01-11 | Dubai Invitational | Dubai | UAE | Asia/Dubai</code>. Strefę możesz też uzupełnić po imporcie.</p>
          <textarea rows={10} value={importText} onChange={(event) => setImportText(event.target.value)} placeholder={`08.01-11.01 Turniej 1 | Europe/London\n15.01-18.01 Turniej 2 | Asia/Dubai`} />
          <div className="season-calendar-import-status">
            <strong>{parsed.rows.length} poprawnych</strong>
            {parsed.errors.length ? <span>{parsed.errors.slice(0, 3).join(" · ")}</span> : <span>Lista gotowa do utworzenia.</span>}
          </div>
          <footer>
            <button type="button" className="secondary-button" onClick={() => setImportOpen(false)}>Anuluj</button>
            <button type="button" className="primary-button" disabled={!parsed.rows.length || Boolean(parsed.errors.length) || bulkCreate.isPending} onClick={() => bulkCreate.mutate()}>{bulkCreate.isPending ? "Tworzę turnieje…" : `Utwórz ${parsed.rows.length} turniejów`}</button>
          </footer>
        </article>
      ) : null}

      {errorMessage ? <article className="panel prep-error"><h3>Nie udało się wczytać lub zapisać kalendarza</h3><p>{errorMessage}</p></article> : null}

      <article className="panel season-calendar-list">
        <header>
          <div><span className="eyebrow">TURNIEJE</span><h3>{sorted.length ? `${sorted.length} wydarzeń` : "Brak turniejów"}</h3></div>
          <small>Bez zapisanej strefy IANA bot nie powinien wykonywać automatycznych operacji czasowych dla nieznanego eventu.</small>
        </header>
        {tournamentsQuery.isPending || timezonesQuery.isPending ? <p>Wczytuję…</p> : null}
        {!tournamentsQuery.isPending && !sorted.length ? <div className="season-calendar-empty">Dodaj turnieje pojedynczo albo zaimportuj cały kalendarz sezonu.</div> : null}
        <div className="season-calendar-rows">
          {sorted.map((item) => {
            const timezoneRow = timezoneById.get(item.id);
            const savedTimezone = timezoneRow?.timezone || "";
            const current = inCurrentWeek(item, savedTimezone);
            const draftTimezone = timezoneDrafts[item.id] ?? savedTimezone;
            const timezoneDirty = Boolean(timezoneRow) && draftTimezone.trim() !== savedTimezone;
            const savingThis = saveTimezone.isPending && saveTimezone.variables?.tournamentId === item.id;
            return (
              <div key={item.id} className={`season-calendar-row ${current ? "current" : ""}`}>
                <span className="season-calendar-date">{dateLabel(item.startDate, item.endDate)}</span>
                <span className="season-calendar-name"><strong>{item.name}</strong><small>Sezon {item.season} · {item.status || "planned"}</small></span>
                <div className={`season-calendar-timezone ${savedTimezone ? "ready" : "missing"}`}>
                  <label>
                    <span>Strefa czasowa eventu</span>
                    <input
                      list="event-timezone-options"
                      value={draftTimezone}
                      onChange={(event) => setTimezoneDrafts((currentDrafts) => ({ ...currentDrafts, [item.id]: event.target.value }))}
                      placeholder="np. Asia/Seoul"
                    />
                  </label>
                  <button
                    type="button"
                    className="secondary-button"
                    disabled={!timezoneRow || !timezoneDirty || savingThis}
                    onClick={() => timezoneRow && saveTimezone.mutate({ tournamentId: item.id, expectedRevision: timezoneRow.configRevision, timezone: draftTimezone })}
                  >
                    {savingThis ? "Zapisuję…" : "Zapisz"}
                  </button>
                </div>
                <div className="season-calendar-row-actions">
                  <span className="season-calendar-bot">{current && savedTimezone ? "BOT: TEN TYDZIEŃ" : !savedTimezone ? "⚠️ BRAK STREFY" : item.status || "planned"}</span>
                  <a className="season-calendar-open" href={`/tournaments?tab=details&tournament=${encodeURIComponent(item.id)}&season=${season}`}>Otwórz →</a>
                </div>
              </div>
            );
          })}
        </div>
      </article>

      <datalist id="event-timezone-options">
        {COMMON_EVENT_TIMEZONES.map((value) => <option key={value} value={value} />)}
      </datalist>

      {creating ? <NewTournamentDialog season={season} tournaments={tournaments} onClose={() => setCreating(false)} onCreated={(_id, createdSeason) => { setSeason(createdSeason); void queryClient.invalidateQueries({ queryKey: ["tournaments", createdSeason] }); void queryClient.invalidateQueries({ queryKey: ["tournament-timezones", createdSeason] }); }} /> : null}
    </section>
  );
}
